import type {
  ReportabilityDecision,
  ReportabilityInput,
  ReportabilityKind,
} from "./contracts";

const REPORTABILITY_REASONS: Record<ReportabilityKind, string> = {
  validation:
    "Validation and user-correctable outcomes stay local unless a caller explicitly escalates them.",
  "expected-user-action":
    "Expected user-driven rejections stay out of GlitchTip unless a caller explicitly escalates them.",
  "api-owned":
    "Valid HTTP responses are owned and reported by the API, so clients suppress duplicate capture unless explicitly escalated.",
  "unexpected-runtime":
    "Unexpected runtime exceptions are reportable because they indicate broken execution.",
  operational:
    "Operational integration failures are reportable because they reflect meaningful system degradation.",
  infrastructure:
    "Infrastructure and dependency failures are reportable because they threaten service health.",
  "impossible-state":
    "Impossible-state errors are reportable because the app reached a broken invariant.",
};

const REPORTABLE_KINDS = new Set<ReportabilityKind>([
  "unexpected-runtime",
  "operational",
  "infrastructure",
  "impossible-state",
]);

/**
 * HTTP statuses that stay breadcrumb-only even though they're "real" server
 * responses: 401 is an expected auth challenge (wrong credentials / expired
 * session) and 422 is expected form validation. Everything else 4xx is
 * treated as a bug signal worth an event during open testing.
 */
const BREADCRUMB_ONLY_HTTP_STATUSES = new Set([401, 422]);

export const EXPECTED_DOMAIN_ERROR_CODES: readonly string[] = Object.freeze([
  "ActiveMembership",
  "ActiveSubscriptionExists",
  "NotMember",
  "InvalidCode",
  "PaymentReconciliationRequired",
  "OverlapNotAllowed",
  "NotClubOperator",
  "StaffInviteNotAcceptable",
  "OwnerOnlyFinancialReport",
]);

const EXPECTED_DOMAIN_ERROR_CODE_LOOKUP: ReadonlySet<string> = new Set(
  EXPECTED_DOMAIN_ERROR_CODES,
);

export function isExpectedDomainErrorCode(code: string | undefined): boolean {
  return code !== undefined && EXPECTED_DOMAIN_ERROR_CODE_LOOKUP.has(code);
}

const HTTP_STATUS_REASONS = {
  expectedDomainError:
    "The API rejected the request with an expected business rule code, so it stays breadcrumb-only.",
  serverError:
    "5xx server responses are reportable because they indicate backend degradation.",
  bugSignalClientError:
    "This 4xx status is not an expected auth/validation outcome, so it is treated as a bug signal and reported.",
  expectedClientError:
    "401 (auth challenge) and 422 (form validation) are expected outcomes; they stay breadcrumb-only to avoid flooding during open testing.",
};

export const REPORTABILITY_TAXONOMY: Readonly<
  Record<ReportabilityKind, ReportabilityDecision>
> = {
  validation: {
    kind: "validation",
    shouldReport: false,
    reason: REPORTABILITY_REASONS.validation,
  },
  "expected-user-action": {
    kind: "expected-user-action",
    shouldReport: false,
    reason: REPORTABILITY_REASONS["expected-user-action"],
  },
  "api-owned": {
    kind: "api-owned",
    shouldReport: false,
    reason: REPORTABILITY_REASONS["api-owned"],
  },
  "unexpected-runtime": {
    kind: "unexpected-runtime",
    shouldReport: true,
    reason: REPORTABILITY_REASONS["unexpected-runtime"],
  },
  operational: {
    kind: "operational",
    shouldReport: true,
    reason: REPORTABILITY_REASONS.operational,
  },
  infrastructure: {
    kind: "infrastructure",
    shouldReport: true,
    reason: REPORTABILITY_REASONS.infrastructure,
  },
  "impossible-state": {
    kind: "impossible-state",
    shouldReport: true,
    reason: REPORTABILITY_REASONS["impossible-state"],
  },
};

export function classifyReportability(
  input: ReportabilityInput,
): ReportabilityDecision {
  if (input.escalate) {
    return {
      kind: input.kind,
      shouldReport: true,
      reason: `${REPORTABILITY_REASONS[input.kind]} Escalated explicitly by the caller.`,
    };
  }

  const status = input.httpStatus;
  if (typeof status === "number" && Number.isFinite(status)) {
    if (status >= 500) {
      return {
        kind: input.kind,
        shouldReport: true,
        reason: HTTP_STATUS_REASONS.serverError,
      };
    }

    if (status >= 400) {
      if (isExpectedDomainErrorCode(input.errorCode)) {
        return {
          kind: "expected-user-action",
          shouldReport: false,
          reason: HTTP_STATUS_REASONS.expectedDomainError,
        };
      }

      return BREADCRUMB_ONLY_HTTP_STATUSES.has(status)
        ? {
            kind: input.kind,
            shouldReport: false,
            reason: HTTP_STATUS_REASONS.expectedClientError,
          }
        : {
            kind: input.kind,
            shouldReport: true,
            reason: HTTP_STATUS_REASONS.bugSignalClientError,
          };
    }
  }

  return REPORTABILITY_TAXONOMY[input.kind];
}

export function shouldReportError(input: ReportabilityInput): boolean {
  if (input.escalate) {
    return true;
  }

  return REPORTABLE_KINDS.has(input.kind);
}
